import React from 'react';
import { Box, Typography, Paper, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useDocumentTitle } from '../hooks/core';

export const TermsOfServicePage: React.FC = () => {
  useDocumentTitle('Terms of Service');
  const effectiveDate = 'December 18, 2025';

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: 1, borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Effective Date: {effectiveDate}
        </Typography>
        <Divider sx={{ my: 3 }} />

        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" paragraph>
            Welcome to SquareUp! These Terms of Service ("Terms") govern your access to and use of the SquareUp platform,
            website, and services (collectively, the "Service") operated by <strong>Katapult Media, Inc.</strong> ("Company," "we," "us," or "our").
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms,
            please do not use the Service.
          </Typography>
        </Box>

        {/* Section 1: Platform Description & Purpose */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            1. Platform Description & Purpose
          </Typography>
          <Typography variant="body1" paragraph>
            SquareUp is a <strong>social game management platform</strong> that provides digital tools for organizing and
            tracking friendly squares pools among known groups of friends, family, and coworkers.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Important:</strong> SquareUp is NOT a gambling, betting, or wagering service. The platform serves
            exclusively as a "scorekeeper" and organizational tool to help users manage their private social games.
            SquareUp does not:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Facilitate, process, or hold any wagers or bets</Typography></li>
            <li><Typography variant="body1">Calculate or display betting odds</Typography></li>
            <li><Typography variant="body1">Take any cut, rake, or commission from pools</Typography></li>
            <li><Typography variant="body1">Enable cash-out or withdrawal mechanisms for game winnings</Typography></li>
            <li><Typography variant="body1">Operate as a licensed gambling establishment</Typography></li>
          </Box>
        </Box>

        {/* Section 2: Non-Gambling Classification */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            2. Non-Gambling Classification
          </Typography>
          <Typography variant="body1" paragraph>
            SquareUp is designed to facilitate social games among private groups and does not constitute gambling
            under applicable law. Key distinctions include:
          </Typography>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Gambling Sites</strong></TableCell>
                  <TableCell><strong>SquareUp</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Open to the general public</TableCell>
                  <TableCell>Private groups only (invite-based)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>House takes a rake/commission</TableCell>
                  <TableCell>No platform cut of pools</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Calculates and displays betting odds</TableCell>
                  <TableCell>No odds calculation or betting lines</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Facilitates real-money wagering</TableCell>
                  <TableCell>Organizes friendly social pools</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Enables cash out of winnings</TableCell>
                  <TableCell>No withdrawal mechanism</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Licensed gambling operator</TableCell>
                  <TableCell>Social game management tool</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body1" paragraph>
            <strong>Credits:</strong> SquareUp credits are used exclusively for platform features such as creating boards
            and purchasing themes. Credits have no cash value, cannot be exchanged for money, and are not used for wagering.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>User Responsibility:</strong> Users are solely responsible for ensuring their use of SquareUp complies
            with all applicable local, state, and federal laws in their jurisdiction. Many jurisdictions have social gambling
            exemptions for private games among friends with no house profit. We encourage users to familiarize themselves
            with the laws in their area.
          </Typography>
        </Box>

        {/* Section 3: User Accounts & Eligibility */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            3. User Accounts & Eligibility
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Age Requirement:</strong> You must be at least 18 years old (or the age of majority in your
            jurisdiction, if higher) to use SquareUp. By creating an account, you represent and warrant that you
            meet this age requirement.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Account Registration:</strong> To access certain features, you must create an account with
            accurate and complete information. You are responsible for maintaining the confidentiality of your
            account credentials and for all activities that occur under your account.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Account Security:</strong> You agree to immediately notify us of any unauthorized use of your
            account or any other breach of security. We are not liable for any loss or damage arising from your
            failure to protect your account credentials.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Restricted Jurisdictions:</strong> SquareUp is not available in jurisdictions where the use of
            such services is prohibited by law. By using the Service, you represent that you are not located in a
            restricted jurisdiction and are not on any government sanctions list.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Account Termination:</strong> We reserve the right to suspend or terminate your account at any
            time, with or without notice, for any reason, including but not limited to violation of these Terms,
            suspected fraud, or illegal activity.
          </Typography>
        </Box>

        {/* Section 4: User Conduct & Acceptable Use */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            4. User Conduct & Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to use SquareUp only for lawful purposes and in accordance with these Terms. You agree NOT to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Use the Service for any illegal purpose or in violation of any applicable laws</Typography></li>
            <li><Typography variant="body1">Engage in fraud, deception, or misrepresentation</Typography></li>
            <li><Typography variant="body1">Harass, abuse, threaten, or intimidate other users</Typography></li>
            <li><Typography variant="body1">Post or transmit harmful, offensive, or objectionable content</Typography></li>
            <li><Typography variant="body1">Attempt to gain unauthorized access to other accounts or systems</Typography></li>
            <li><Typography variant="body1">Interfere with or disrupt the Service or servers</Typography></li>
            <li><Typography variant="body1">Use automated means (bots, scrapers) to access the Service</Typography></li>
            <li><Typography variant="body1">Circumvent any security or access controls</Typography></li>
            <li><Typography variant="body1">Impersonate any person or entity</Typography></li>
            <li><Typography variant="body1">Use the Service in any manner that could be construed as unlawful gambling</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            <strong>Fair Play:</strong> Users are expected to participate honestly and in good faith. Manipulation
            of boards, collusion, or other unfair practices are prohibited.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Reporting:</strong> If you become aware of any violation of these Terms or illegal activity,
            please report it to us immediately at support@katapultmedia.com.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Consequences:</strong> Violations may result in warnings, suspension, or permanent termination
            of your account, and may be reported to appropriate authorities.
          </Typography>
        </Box>

        {/* Section 5: Board Management & Participation */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            5. Board Management & Participation
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Board Owners:</strong> Users who create boards ("Board Owners") are responsible for:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Managing their board and participants fairly</Typography></li>
            <li><Typography variant="body1">Setting appropriate rules and payout structures</Typography></li>
            <li><Typography variant="body1">Ensuring participants are known to them personally</Typography></li>
            <li><Typography variant="body1">Handling all payments and distributions outside the platform</Typography></li>
            <li><Typography variant="body1">Complying with all applicable laws in their jurisdiction</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            <strong>Participants:</strong> Users who join boards agree to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Abide by the rules set by the Board Owner</Typography></li>
            <li><Typography variant="body1">Participate honestly and in good faith</Typography></li>
            <li><Typography variant="body1">Handle payment obligations directly with the Board Owner</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            <strong>Dispute Resolution:</strong> SquareUp provides the organizational tools only. Any disputes
            between users regarding payments, winnings, or board management are solely between those users.
            SquareUp is not responsible for resolving disputes between users and will not mediate or arbitrate
            such matters.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Offline Players:</strong> Board Owners may add "offline players" who do not have SquareUp
            accounts. Board Owners are fully responsible for managing offline players and their participation.
          </Typography>
        </Box>

        {/* Section 6: Payments & Credits */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            6. Payments & Credits
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Credit System:</strong> SquareUp uses a credit system for platform features. Credits may be
            purchased through our secure payment processor (Stripe) and are used to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Create new boards</Typography></li>
            <li><Typography variant="body1">Purchase premium themes</Typography></li>
            <li><Typography variant="body1">Access other premium features as they become available</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            <strong>No Cash Value:</strong> Credits have no cash value and cannot be redeemed for money.
            Credits cannot be transferred between accounts. There is no mechanism to withdraw or "cash out" credits.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Refund Policy:</strong> Credit purchases are generally non-refundable. Refund requests will
            be considered on a case-by-case basis at our sole discretion. Credits for boards deleted while in
            draft status (before going live) will be returned to your account.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Payment Processing:</strong> All payments are processed securely by Stripe. By making a
            purchase, you agree to Stripe's terms of service. We do not store credit card information on our servers.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Promotional Credits:</strong> We may offer promotional credits, coupons, or bonuses from
            time to time. These may be subject to additional terms, expiration dates, or usage restrictions
            as specified when granted.
          </Typography>
        </Box>

        {/* Section 7: Intellectual Property */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            7. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Platform Ownership:</strong> SquareUp and all related trademarks, service marks, logos,
            graphics, and content are the property of Katapult Media, Inc. or its licensors. You may not use,
            copy, or distribute any of our intellectual property without prior written consent.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>User Content:</strong> You retain ownership of content you create (such as board names and
            descriptions). By posting content, you grant us a non-exclusive, royalty-free license to use,
            display, and distribute that content in connection with operating the Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>DMCA:</strong> We respect intellectual property rights and will respond to valid takedown
            notices in accordance with the Digital Millennium Copyright Act. To report infringement, contact
            legal@katapultmedia.com.
          </Typography>
        </Box>

        {/* Section 8: Disclaimers & Limitations */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            8. Disclaimers & Limitations of Liability
          </Typography>
          <Typography variant="body1" paragraph sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
            OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </Typography>
          <Typography variant="body1" paragraph>
            We do not warrant that the Service will be uninterrupted, secure, or error-free. We are not
            responsible for any losses or damages arising from:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Service interruptions or unavailability</Typography></li>
            <li><Typography variant="body1">Errors, bugs, or inaccuracies in the Service</Typography></li>
            <li><Typography variant="body1">Loss of data or content</Typography></li>
            <li><Typography variant="body1">Disputes between users</Typography></li>
            <li><Typography variant="body1">Actions of third parties</Typography></li>
            <li><Typography variant="body1">Your violation of applicable laws</Typography></li>
          </Box>
          <Typography variant="body1" paragraph sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, KATAPULT MEDIA, INC. SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
            WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE
            LOSSES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE
            SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Indemnification:</strong> You agree to indemnify, defend, and hold harmless Katapult Media, Inc.
            and its officers, directors, employees, agents, and affiliates from any claims, damages, losses,
            liabilities, and expenses (including legal fees) arising from your use of the Service, violation
            of these Terms, or infringement of any rights of another party.
          </Typography>
        </Box>

        {/* Section 9: Modifications & Termination */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            9. Modifications & Termination
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Changes to Terms:</strong> We may modify these Terms at any time. We will notify you of
            material changes by posting the updated Terms on the Service and updating the "Effective Date."
            Your continued use of the Service after such changes constitutes acceptance of the new Terms.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Service Changes:</strong> We reserve the right to modify, suspend, or discontinue any
            aspect of the Service at any time, with or without notice. We are not liable to you or any
            third party for any modification, suspension, or discontinuation.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Termination:</strong> You may terminate your account at any time by contacting us. Upon
            termination, your right to use the Service immediately ceases. Provisions that by their nature
            should survive termination (including disclaimers, limitations of liability, and indemnification)
            shall survive.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Data Retention:</strong> Upon account termination, we may retain certain information as
            required by law or for legitimate business purposes. See our Privacy Policy for details.
          </Typography>
        </Box>

        {/* Section 10: Governing Law & Disputes */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            10. Governing Law & Dispute Resolution
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with
            the laws of the State of Delaware, without regard to its conflict of law provisions.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to
            these Terms or the Service shall be resolved by binding arbitration administered by the American
            Arbitration Association in accordance with its Commercial Arbitration Rules. The arbitration shall
            be conducted in Wilmington, Delaware. Judgment on the arbitration award may be entered in any
            court of competent jurisdiction.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Class Action Waiver:</strong> YOU AGREE THAT ANY CLAIMS WILL BE BROUGHT IN YOUR INDIVIDUAL
            CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Small Claims Exception:</strong> Notwithstanding the above, either party may bring an
            individual action in small claims court for claims within that court's jurisdiction.
          </Typography>
        </Box>

        {/* Section 11: General Provisions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            11. General Provisions
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the
            entire agreement between you and Katapult Media, Inc. regarding the Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the
            remaining provisions will remain in full force and effect.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not
            constitute a waiver of such right or provision.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Assignment:</strong> You may not assign or transfer these Terms without our prior written
            consent. We may assign our rights and obligations without restriction.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Force Majeure:</strong> We shall not be liable for any failure or delay in performance
            due to circumstances beyond our reasonable control, including but not limited to acts of God,
            natural disasters, war, terrorism, labor disputes, or government actions.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            12. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms of Service, please contact us:
          </Typography>
          <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
            <Typography variant="body1">
              <strong>Katapult Media, Inc.</strong>
            </Typography>
            <Typography variant="body1">
              Email: legal@katapultmedia.com
            </Typography>
            <Typography variant="body1">
              Support: support@katapultmedia.com
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          By using SquareUp, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
        </Typography>
      </Paper>
    </Box>
  );
};

export default TermsOfServicePage;
